import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import PageLinkCTA from "@/components/PageLinkCTA";
import SuccessCheck from "@/components/SuccessCheck";
import { Reveal } from "@/components/Reveal";
import { POSITION_CATEGORIES } from "@/data/careers";
import { postJSON } from "@/lib/api";

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  position: "",
  qualification: "",
  experience: "",
  message: "",
};

export default function Careers() {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      setError("Resume must be under 6 MB.");
      return;
    }
    setError("");
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.email || !form.phone || !form.position) {
      setError("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      let payload = { ...form };
      if (file) {
        const asBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        payload = {
          ...payload,
          resume_filename: file.name,
          resume_content_type: file.type || "application/octet-stream",
          resume_content: asBase64,
        };
      }
      await postJSON("/applications", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Could not submit your application right now. Please try again, or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm(INITIAL);
    setFile(null);
    setDone(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
      <PageHero
        eyebrow="Careers"
        title="Build Your Career With Bik Commotrade"
        description="We welcome talented and motivated individuals interested in engineering, manufacturing, railway products, quality, operations, sales and business development."
      />

      <section className="relative bg-[#F3F7FB] pb-24" data-testid="careers-section">
        <div className="wrap relative">
          <Reveal>
            <div className="flex flex-wrap gap-2.5" data-testid="careers-categories">
              {POSITION_CATEGORIES.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-navy-900/12 bg-navy-900/[0.03] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600 transition hover:border-brand-emeraldLight/60 hover:text-brand-emeraldDark"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-slate-500">
              Positions are listed as categories of interest — we review applications on a rolling basis against
              current and future requirements.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mx-auto mt-12 max-w-3xl overflow-hidden rounded-3xl border border-navy-900/10 bg-white/70 backdrop-blur-md">
              {done ? (
                <div className="p-8 sm:p-12">
                  <SuccessCheck
                    title="Application Received"
                    message="Thank you for applying. Our team will review your profile and reach out to you at the email address provided."
                  />
                  <div className="mt-8 text-center">
                    <button type="button" onClick={reset} className="btn btn-outline" data-testid="careers-apply-again">
                      Submit Another Application
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="p-6 sm:p-10" data-testid="careers-apply-form" noValidate>
                  <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-ink">
                    Application Form
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Fields marked * are required. Your resume travels securely with your application.
                  </p>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor="careers-name">Full Name *</label>
                      <input id="careers-name" className="field" data-testid="careers-full-name-input" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="careers-email">Email *</label>
                      <input id="careers-email" type="email" className="field" data-testid="careers-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="careers-phone">Phone *</label>
                      <input id="careers-phone" type="tel" className="field" data-testid="careers-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 …" required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="careers-position">Position Applying For *</label>
                      <select id="careers-position" className="field appearance-none" data-testid="careers-position-select" value={form.position} onChange={set("position")} required>
                        <option value="" disabled className="bg-white">Select a category</option>
                        {POSITION_CATEGORIES.map((c) => (
                          <option key={c} value={c} className="bg-white">{c}</option>
                        ))}
                        <option value="General Application" className="bg-white">General Application</option>
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="careers-qualification">Qualification</label>
                      <input id="careers-qualification" className="field" data-testid="careers-qualification-input" value={form.qualification} onChange={set("qualification")} placeholder="e.g. B.Tech, Diploma, ITI" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="careers-experience">Experience</label>
                      <input id="careers-experience" className="field" data-testid="careers-experience-input" value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years" />
                    </div>

                    <div className="sm:col-span-2">
                      <span className="field-label">Resume Upload</span>
                      <label
                        htmlFor="careers-resume"
                        data-testid="careers-resume-dropzone"
                        className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-navy-900/20 bg-navy-900/[0.02] px-6 py-8 text-center transition hover:border-brand-emeraldLight/60 hover:bg-navy-900/[0.03]"
                      >
                        <UploadCloud className="h-8 w-8 text-brand-emeraldDark" />
                        {file ? (
                          <span className="text-sm text-slate-600">
                            <strong className="text-brand-emeraldDark">{file.name}</strong> —{" "}
                            {(file.size / 1024).toFixed(0)} KB (click to replace)
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">
                            Click to upload — PDF, DOC or DOCX, up to 6 MB
                          </span>
                        )}
                        <input
                          ref={fileRef}
                          id="careers-resume"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          data-testid="careers-resume-input"
                          onChange={onFile}
                        />
                      </label>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="careers-message">Message</label>
                      <textarea id="careers-message" rows={4} className="field resize-none" data-testid="careers-message-input" value={form.message} onChange={set("message")} placeholder="Tell us briefly about yourself and your interest in railway engineering" />
                    </div>
                  </div>

                  {error && (
                    <p className="mt-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="careers-form-error" role="alert">
                      {error}
                    </p>
                  )}

                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={sending}
                    data-testid="careers-submit-application-button"
                    className="btn btn-primary mt-8 w-full disabled:cursor-wait disabled:opacity-70"
                  >
                    {sending ? "Submitting…" : "Submit Application"}
                    <Send className="h-4 w-4" />
                  </motion.button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="wrap section-pad">
        <PageLinkCTA />
      </section>
    </>
  );
}
